import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

interface CreateTeamMemberParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createTeamMemberAPI =
  ({ payload, params, onSuccess, onError }: CreateTeamMemberParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.post(`/users`, payload, { params });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Team Member created successfully");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to create Team Member");
          onError?.();
        }
      } catch (error: any) {
        console.error("Create Team Member error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to create Team Member";
        message.error(errorMessage);
      }
    };

interface UpdateTeamMemberParams {
  payload: any;
  params?: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateTeamMemberAPI =
  ({ payload, params, onSuccess, onError }: UpdateTeamMemberParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.put(`/users`, payload, { params });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message);
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        console.error("Update user error:", error);
        onError?.();

        const errorMessage = error?.response?.data?.message;
        message.error(errorMessage);
      }
    };

export interface TeamMemberFilterParams {
  search?: string;
  role?: string;
  is_active?: string;
  isActive?: boolean | string;
  page?: number;
  limit?: number;
  sortBy?: string | null;
  sortOrder?: string | null;
  roleKey?: string;
}

interface GetTeamMemberListParams {
  filters: TeamMemberFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getTeamMembersListAPI =
  ({ filters, onSuccess, onError, signal }: GetTeamMemberListParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/users", {
          params: filters,
          signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Team Members List Error:", error);
        message.error(error?.response?.data?.message);
        onError?.();
      }
    };

interface ToggleArchiveTeamMemberParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const ToggleArchiveTeamMemberAPI =
  ({ payload, onSuccess, onError }: ToggleArchiveTeamMemberParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.post(`/users/toggle-archive`, payload);

        let result = responseDataFormate(response.data);

        if (result?.success) {
          message.success(result?.message || "Team Member archive status updated");
          onSuccess?.(result.data);
        } else {
          message.error(result?.message || "Failed to update archive status");
          onError?.();
        }
      } catch (error: any) {
        console.error("Toggle Archive Team Member error:", error);
        onError?.();

        const errorMessage =
          error?.response?.data?.message || "Unable to update archive status";
        message.error(errorMessage);
      }
    };

interface ExportSage50PayrollEmployeesParams {
  payload: any;
  onSuccess?: () => void;
  onError?: (errorObj?: any) => void;
}

export const exportSage50PayrollEmployeesAPI =
  ({ payload, onSuccess, onError }: ExportSage50PayrollEmployeesParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.post(
          `/users/export-sage50-payroll-employees`,
          payload,
          {
            responseType: "blob",
          },
        );

        let fileName = "Sage50_Payroll_Employees.csv";

        const contentDisposition = response.headers["content-disposition"];

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const blob = new Blob([response.data]);

        if (Capacitor.isNativePlatform()) {
          const reader = new FileReader();

          const blobToBase64 = (blob: Blob) =>
            new Promise((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

          try {
            const result: any = await blobToBase64(blob);
            const base64Data = result?.split(",")[1];

            const uniqueFileName = `${Date.now()}_${fileName}`;

            const savedFile = await Filesystem.writeFile({
              path: uniqueFileName,
              data: base64Data,
              directory: Directory.Cache,
            });

            await Share.share({
              url: savedFile.uri,
            });

            onSuccess?.();
          } catch (err) {
            console.error("Native Share Error:", err);
            onError?.();
          }
        } else {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();

          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);

          onSuccess?.();
          message.success("File downloaded successfully!");
        }
      } catch (error: any) {
        console.error("Export error:", error);

        if (error.response && error.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorObj = JSON.parse(reader.result as string);
              message.error(errorObj.message || "Failed to export data");
              onError?.(errorObj);
            } catch (e) {
              message.error("Failed to export data");
              onError?.();
            }
          };
          reader.readAsText(error.response.data);
        } else {
          const errorMessage = error?.response?.data?.message;
          message.error(errorMessage || "Failed to export data");
          onError?.(error?.response?.data);
        }
      }
    };

export interface ExportManageAllowanceParams {
  payload?: any;
  onSuccess?: () => void;
  onError?: (errorObj?: any) => void;
}

export const exportManageAllowanceAPI =
  ({ payload, onSuccess, onError }: ExportManageAllowanceParams) =>
    async (dispatch?: any) => {
      try {
        const response = await axiosInstance.post(
          `/users/export-manage-allowance`,
          payload,
          {
            responseType: "blob",
          },
        );

        let fileName = "Manage_Allowance.xlsx";

        const contentDisposition = response.headers["content-disposition"];

        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        if (Capacitor.isNativePlatform()) {
          const reader = new FileReader();

          const blobToBase64 = (blob: Blob) =>
            new Promise((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

          try {
            const result: any = await blobToBase64(blob);
            const base64Data = result?.split(",")[1];

            const uniqueFileName = `${Date.now()}_${fileName}`;

            const savedFile = await Filesystem.writeFile({
              path: uniqueFileName,
              data: base64Data,
              directory: Directory.Cache,
            });

            await Share.share({
              url: savedFile.uri,
            });

            onSuccess?.();
          } catch (err) {
            console.error("Native Share Error:", err);
            onError?.();
          }
        } else {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();

          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);

          onSuccess?.();
          message.success("File downloaded successfully!");
        }
      } catch (error: any) {
        console.error("Export error:", error);

        if (error.response && error.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorObj = JSON.parse(reader.result as string);
              message.error(errorObj.message || "Failed to export data");
              onError?.(errorObj);
            } catch (e) {
              message.error("Failed to export data");
              onError?.();
            }
          };
          reader.readAsText(error.response.data);
        } else {
          const errorMessage = error?.response?.data?.message;
          message.error(errorMessage || "Failed to export data");
          onError?.(error?.response?.data);
        }
      }
    };



export interface GetCalendarEventsFilterParams {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
}

interface GetCalendarEventsAPIParams {
  filters: GetCalendarEventsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getCalendarEventsAPI =
  ({ filters, onSuccess, onError, signal }: GetCalendarEventsAPIParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/clock-in-history/non-working-days", {
          params: filters,
          signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Calendar Events API Error:", error);
        message.error(error?.response?.data?.message);
        onError?.();
      }
    };


export interface GetCalendarEventDetailsFilterParams {
  id: string;
  type: string;
}

interface GetCalendarEventDetailsAPIParams {
  filters: GetCalendarEventDetailsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getCalendarEventDetailsAPI =
  ({ filters, onSuccess, onError, signal }: GetCalendarEventDetailsAPIParams) =>
    async (dispatch: any) => {
      try {
        const response = await axiosInstance.get("/clock-in-history/calender-event-details", {
          params: filters,
          signal,
        });

        let result = responseDataFormate(response.data);

        if (result?.success) {
          onSuccess?.(result.data);
        } else {
          message.error(result?.message);
          onError?.();
        }
      } catch (error: any) {
        if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
        console.error("Get Calendar Event Details API Error:", error);
        message.error(error?.response?.data?.message);
        onError?.();
      }
    };


